import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/free-solid-svg-icons';

import {
  SampleProjectView,
  type ProjectPageSampleSource,
} from './PdfProjectPage';
import { getSampleProject } from '../samples/sampleProjects';
import type { ScoreSystemData } from '../hooks/usePdfStateQueue';
import {
  SCORE_CTA_PRIMARY_BUTTON_CLASS,
  ScoreCtaInner,
} from '../components/project/ScoreCtaBox';
import { usePageMeta } from '../hooks/usePageMeta';
import { getSamplePageMeta } from '../seo/publicPageMeta';
import { trackButtonClick } from '../services/telemetry';
import { sampleHomePath } from '../samples/sampleReturnTo';

const saveUrlAs = async (url: string, filename: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download ${filename} (${response.status})`);
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/html')) {
    throw new Error(`Got HTML instead of a file for ${filename}`);
  }
  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

const fetchAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load ${url} (${response.status})`);
  }
  const blob = await response.blob();
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(reader.error ?? new Error(`Failed to read ${url}`));
    reader.readAsDataURL(blob);
  });
  const comma = dataUrl.indexOf(',');
  if (comma === -1) {
    throw new Error(`Expected a data URL for ${url}`);
  }
  return dataUrl.slice(comma + 1);
};

function SampleFooterCta({ slug, homeTo }: { slug: string; homeTo: string }) {
  return (
    <ScoreCtaInner
      title="Now try it on your own music."
      subtitle="Snap a photo or upload a score."
    >
      <div
        className={`
          flex flex-wrap items-center justify-center gap-2
          md:gap-3
          lg:gap-4
        `}
      >
        <Link
          to={homeTo}
          onClick={() => {
            void trackButtonClick('SampleProject', 'Try it on your own piece', {
              slug,
            });
          }}
          className={SCORE_CTA_PRIMARY_BUTTON_CLASS}
        >
          <FontAwesomeIcon
            icon={faArrowUpFromBracket}
            aria-hidden="true"
            className="text-xs md:text-sm lg:text-base"
          />
          Try it on my own piece
        </Link>
        <Link
          to="/sign-up"
          onClick={() => {
            void trackButtonClick('SampleProject', 'Create a free account', {
              slug,
            });
          }}
          className={`
            inline-flex items-center justify-center rounded-full border
            border-gray-300 px-3.5 py-1.5 text-sm font-medium text-gray-700
            transition-colors duration-100
            hover:border-gray-400 hover:bg-gray-50
            active:scale-[0.985]
            md:px-5 md:py-2.5 md:text-base
            lg:px-6 lg:py-3.5 lg:text-lg
          `}
        >
          Create a free account
        </Link>
      </div>
    </ScoreCtaInner>
  );
}

function SampleStatus({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={`
        flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center gap-4
        bg-[#E6E7E7] px-6 text-center
      `}
    >
      {title && <p className="text-lg font-semibold text-gray-800">{title}</p>}
      {children}
    </div>
  );
}

/** Public `/sample/:slug` page. Renders the real project UI from a static snapshot. */
export const SampleProjectPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { state } = useLocation();
  const homeTo = sampleHomePath(state);
  const sampleMeta = getSampleProject(slug);

  const [fingeringData, setFingeringData] = useState<ScoreSystemData[] | null>(
    null
  );
  const [loadError, setLoadError] = useState<Error | null>(null);

  const fingeringUrl = sampleMeta?.fingeringUrl;
  const expectedSystems = sampleMeta?.pageSrcs.length;

  useEffect(() => {
    if (!fingeringUrl || expectedSystems === undefined) return;
    let cancelled = false;

    fetch(fingeringUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load sample (${response.status})`);
        }
        return response.json() as Promise<unknown>;
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error('Sample fingering data is not an array');
        }
        if (data.length !== expectedSystems) {
          throw new Error(
            `Sample fingering has ${data.length} systems, expected ${expectedSystems}`
          );
        }
        if (!cancelled) setFingeringData(data as ScoreSystemData[]);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(
            error instanceof Error ? error : new Error(String(error))
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fingeringUrl, expectedSystems]);

  const getPdfPages = useCallback(() => {
    if (!sampleMeta) {
      return Promise.reject(new Error('Sample is not loaded'));
    }
    return Promise.all(sampleMeta.pageSrcs.map(fetchAsBase64));
  }, [sampleMeta]);

  const downloadXml = useCallback(
    async (format: 'standard' | 'sibelius' | 'plain') => {
      if (!sampleMeta) {
        throw new Error('Sample is not loaded');
      }
      if (format === 'plain') {
        throw new Error('Clean MusicXML is not available for sample scores');
      }
      const isSibelius = format === 'sibelius';
      await saveUrlAs(
        isSibelius ? sampleMeta.xmlSibeliusUrl : sampleMeta.xmlUrl,
        `${sampleMeta.downloadBaseName}${
          isSibelius ? '_fingering_sibelius' : '_fingering'
        }.xml`
      );
    },
    [sampleMeta]
  );

  const source: ProjectPageSampleSource | null = useMemo(() => {
    if (!sampleMeta || !fingeringData) return null;
    return {
      slug: sampleMeta.slug,
      title: sampleMeta.title,
      pageSrcs: sampleMeta.pageSrcs,
      fingeringData,
      getPdfPages,
      downloadXml,
      backTo: homeTo,
      footer: <SampleFooterCta slug={sampleMeta.slug} homeTo={homeTo} />,
    };
  }, [sampleMeta, fingeringData, getPdfPages, downloadXml, homeTo]);

  const pageMeta = getSamplePageMeta(slug) ?? {
    title: 'Sample not found - PineappleMusicLab',
    description: 'This sample score is not available.',
    path: `/sample/${slug ?? ''}`,
  };
  usePageMeta(pageMeta);

  if (!sampleMeta) {
    return (
      <SampleStatus title="We don't have that sample.">
        <Link to={homeTo} className="font-semibold text-yellow-700 underline">
          Back to home
        </Link>
      </SampleStatus>
    );
  }

  if (loadError) {
    return (
      <SampleStatus title="We couldn't load this sample.">
        <Link to={homeTo} className="font-semibold text-yellow-700 underline">
          Back to home
        </Link>
      </SampleStatus>
    );
  }

  if (!source) {
    return (
      <SampleStatus>
        <div
          className={`
            h-10 w-10 animate-spin rounded-full border-4 border-gray-300
            border-t-yellow-400
          `}
          role="status"
          aria-label="Loading sample score"
        />
      </SampleStatus>
    );
  }

  return <SampleProjectView sample={source} />;
};
